import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { getGameweeks } from '@/api/gameweeks';
import { getCurrentSeason } from '@/api/seasons';
import type { GameweekDto } from '@/types/gameweek';

export enum GameweekStatus {
  CLOSED = 'closed',
  CURRENT = 'current',
  UPCOMING = 'upcoming',
}

export interface GameweekWithStatus extends GameweekDto {
  status: GameweekStatus;
}

export type GroupedGameweeksByStatus = Record<GameweekStatus, GameweekWithStatus[]>;

function resolveDefaultGameweek(list: GameweekDto[]): GameweekWithStatus | null {
  const now = Date.now();

  const next = list.find((gw) => gw.is_next);
  if (next) return { ...next, status: GameweekStatus.CURRENT };

  const current = list.find(
    (gw) => gw.is_current && gw.deadline_time && new Date(gw.deadline_time).getTime() < now,
  );
  if (current) return { ...current, status: GameweekStatus.CURRENT };

  const lastFinished = [...list].reverse().find((gw) => gw.finished || gw.is_previous);
  if (lastFinished) return { ...lastFinished, status: GameweekStatus.CLOSED };

  return null;
}

export function useGameweekStatuses() {
  const seasonQuery = useQuery({
    queryKey: ['season', 'current'],
    queryFn: getCurrentSeason,
  });

  const listQuery = useQuery({
    queryKey: ['gameweeks', 'list', { seasonId: seasonQuery.data?.id }],
    queryFn: async () => {
      const res = await getGameweeks({ season_id: seasonQuery.data!.id, limit: 100, offset: 0, order: 'number.asc' });
      return res.gameweeks;
    },
    enabled: !!seasonQuery.data?.id,
    staleTime: 60 * 1000,
  });

  const groupedGameweeks: GroupedGameweeksByStatus = useMemo(() => {
    const result: GroupedGameweeksByStatus = {
      [GameweekStatus.CURRENT]: [],
      [GameweekStatus.UPCOMING]: [],
      [GameweekStatus.CLOSED]: [],
    };
    (listQuery.data || []).forEach((gw) => {
      let status: GameweekStatus;
      if (gw.is_current) {
        status = GameweekStatus.CURRENT;
      } else if (gw.is_next) {
        status = GameweekStatus.UPCOMING;
      } else if (gw.finished || gw.is_previous) {
        status = GameweekStatus.CLOSED;
      } else {
        status = GameweekStatus.UPCOMING;
      }
      result[status].push({ ...gw, status });
    });
    return result;
  }, [listQuery.data]);

  const [selectedGameweek, setSelectedGameweek] = useState<GameweekWithStatus | null>(null);

  const currentGameweek: GameweekWithStatus | null = useMemo(
    () => resolveDefaultGameweek(listQuery.data || []),
    [listQuery.data],
  );

  const isOffSeason = useMemo(() => {
    const list = listQuery.data || [];
    return list.length > 0 && !list.some((gw) => gw.is_next);
  }, [listQuery.data]);

  const loading = seasonQuery.isLoading || listQuery.isLoading;
  const error = seasonQuery.error || listQuery.error || null;

  return {
    groupedGameweeks,
    currentGameweek,
    selectedGameweek: selectedGameweek || currentGameweek,
    setSelectedGameweek,
    isOffSeason,
    loading,
    error: error ? (error as Error).message : null,
  };
}
